import React from "react";
import styled from "@emotion/styled";
import gql from "graphql-tag";
import { useQuery } from "react-apollo";
import { useToggle } from "@hooks";
import { RailsUrl } from "@services";
import { Box, Button, Container, Flex, Text, Circle } from "@atoms";
import { ImageListItem } from "@molecules";
import { TeamOnboardingChecklistItem } from "@types";

// == Types ================================================================

interface IProps {
  fundingUrl: string;
  isMobile: boolean;
}

// == Constants ============================================================

OnboardingStepper.fragment = gql`
  fragment TeamOnboardingFragment on Viewer {
    id
    isCaptain
    activeCampaign {
      id
      handlesMoney
    }
    teamOnboardingInfo {
      isOnboardingActive
      isOnboardingChecklistActive
      onboardingSteps {
        id
        name
        isCompleted
        description
      }
      onboardingChecklistItems {
        id
        name
        description
        isCompleted
      }
    }
  }
`;

const QUERY = gql`
  query TeamOnboardingStepperQuery {
    Viewer {
      id
      ...TeamOnboardingFragment
    }
  }
  ${OnboardingStepper.fragment}
`;

const getChecklistUrl = (
  checklistItem: TeamOnboardingChecklistItem,
  fundingUrl: string,
  campaignId?: string,
  handlesMoney?: boolean
) => {
  switch (checklistItem) {
    case TeamOnboardingChecklistItem.CAPTAIN_INVITE_TEAM:
    case TeamOnboardingChecklistItem.TEAM_MEMBER_INVITE_SUPPORTERS:
      return `${fundingUrl}/teams/invite`;
    case TeamOnboardingChecklistItem.CAPTAIN_SHOP:
    case TeamOnboardingChecklistItem.TEAM_MEMBER_SHOP:
      return RailsUrl.storeUrl({ id: "firstshop", campaignId });
    case TeamOnboardingChecklistItem.CAPTAIN_SUBMIT_PAYMENT_INFO:
      if (!campaignId || !handlesMoney) return null;
      return RailsUrl.newBeneficiaryUrl(campaignId);
    case TeamOnboardingChecklistItem.CAPTAIN_SET_ROSTER:
      return `${fundingUrl}/teams/team-roster/edit`;
    default:
      return null;
  }
};

// == Component ============================================================

export function OnboardingStepper({ fundingUrl, isMobile }: IProps) {
  const [isOpen, toggleIsOpen] = useToggle(false);

  const bottomOffset = isMobile ? 79 : 0;

  const { data } = useQuery(QUERY);
  const viewer = data?.Viewer;
  if (!viewer) return null;
  if (viewer?.teamOnboardingInfo.isOnboardingActive) return null;
  if (!viewer?.teamOnboardingInfo.isOnboardingChecklistActive || !viewer?.isCaptain) return null;

  const { onboardingSteps, onboardingChecklistItems } = viewer.teamOnboardingInfo;

  const steps = [...onboardingSteps, ...onboardingChecklistItems].map((item) => {
    return {
      ...item,
      url: getChecklistUrl(
        item.id,
        fundingUrl,
        viewer.activeCampaign?.id,
        viewer.activeCampaign?.handlesMoney
      ),
    };
  });
  const remainingTasksCount = steps.reduce(
    (count, step) => (step.isCompleted ? count : count + 1),
    0
  );
  if (!remainingTasksCount) return null;

  return (
    <>
      <style
        // eslint-disable-next-line react/no-danger
        //  overflow: ${isOpen ? "hidden" : "auto"}
        dangerouslySetInnerHTML={{
          __html: `
            body { padding-bottom: 80px; overflow: ${isOpen ? "hidden" : "initial"}; }
            @media screen and (min-width: 640px) {
              body {
                overflow: initial;
              }
            }
`,
        }}
      />
      <Box
        bg="white"
        bottom={bottomOffset}
        left={0}
        position="fixed"
        right={0}
        zIndex={3}
        overflowY="auto"
      >
        <Box bg="orange" py="12px" onClick={toggleIsOpen} style={{ cursor: "pointer" }}>
          <Container>
            <Flex width="100%" alignItems="center" justifyContent="space-between">
              <Text color="white" fontSize={3}>
                {remainingTasksCount} remaining {remainingTasksCount === 1 ? "task" : "tasks"}
              </Text>
              <Box style={{ flexShrink: 0 }}>
                <Button
                  border="none"
                  variant="promoClear"
                  css={{
                    ":focus": {
                      outline: 0,
                      boxShadow: "none",
                    },
                  }}
                >
                  <Text color="orange">{isOpen ? "Close" : "Complete Setup"}</Text>
                </Button>
              </Box>
            </Flex>
          </Container>
        </Box>
        <Box
          height="fit-content"
          maxHeight={isOpen ? "700px" : 0}
          style={{
            transition: "max-height 0.5s ease",
            overflowY: "auto",
          }}
          bg="orange"
        >
          <Box py={3}>
            <Container>
              {steps.map((step, index) => {
                const description = steps[index].description;
                if (!description) return null;

                return (
                  <Box key={step.id} mb={4}>
                    <StyledImageListItem
                      href={step.url}
                      leftComponent={
                        step.isCompleted ? (
                          <Circle bg="white" border="none" width="100%">
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "5px",
                                height: "5px",
                              }}
                            >
                              <i className="material-icons d-flex" style={{ color: "orange" }}>
                                check
                              </i>
                            </div>
                          </Circle>
                        ) : (
                          <Circle bg="orange" border="white 1px solid" width="100%" />
                        )
                      }
                      subText={description}
                      leftComponentProps={{
                        width: "20px",
                        height: "20px",
                        alignSelf: "flex-start",
                      }}
                      text={step.name}
                    />
                  </Box>
                );
              })}
            </Container>
          </Box>
        </Box>
      </Box>
    </>
  );
}

// == Styles ===============================================================
const StyledImageListItem = styled(ImageListItem)`
  .image-list-item-react__title {
    color: ${({ theme }) => theme.colors.white};
    font-weight: bold;
  }
  .image-list-item__subtext {
    color: ${({ theme }) => theme.colors.white};
    white-space: initial;
    display: block;
    line-height: 1.3;
    margin-top: 3px;
  }
`;
